/**
 * useCropImage
 *
 * Converts a crop area (pixels) + rotation into a Blob using the Canvas API.
 * Framework-agnostic — no React state, no side effects.
 *
 * @param imageSrc  - object URL or data URL of the source image
 * @param pixelCrop - crop rectangle in image pixel coordinates (from react-easy-crop)
 * @param rotation  - rotation in degrees (0, 90, 180, 270)
 * @param mimeType  - output MIME type. Default: 'image/jpeg'
 * @param quality   - compression quality 0–1 for jpeg/webp. Default: 0.92
 * @returns Promise<Blob> of the cropped image
 */

import type { Area } from 'react-easy-crop';

export async function getCroppedBlob(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp' = 'image/jpeg',
  quality = 0.92,
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Canvas 2D context not available');

  const { width: bBoxW, height: bBoxH } = getBoundingBox(image.width, image.height, rotation);

  canvas.width = bBoxW;
  canvas.height = bBoxH;

  ctx.translate(bBoxW / 2, bBoxH / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  const croppedCtx = croppedCanvas.getContext('2d');
  if (!croppedCtx) throw new Error('Canvas 2D context not available');

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    croppedCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob returned null'));
      },
      mimeType,
      quality,
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = src;
  });
}

function getBoundingBox(
  width: number,
  height: number,
  rotation: number,
): { width: number; height: number } {
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  return {
    width: Math.round(width * cos + height * sin),
    height: Math.round(width * sin + height * cos),
  };
}
